/**
 * A return path the application may navigate to after sign-in, or null.
 *
 * Only a path on THIS origin is accepted, and only in the plain form a person
 * would recognise: it starts with one `/`, carries no backslash and no control
 * character, and survives URL parsing without acquiring another origin. So
 * `//evil.example`, `/\evil.example`, `https://evil.example` and
 * `javascript:…` are all refused rather than repaired — a return path that
 * needed fixing was not one this application wrote.
 *
 * The sign-in alias itself is refused too, so `/login?next=/login?next=…`
 * cannot bounce between two redirects.
 */
const PROBE_ORIGIN = 'https://return-path.invalid';

/** A backslash, or a C0 control character or DEL anywhere in the value. */
function hasUnsafeCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code <= 0x1f || code === 0x7f || code === 0x5c) return true;
  }

  return false;
}

export function safeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (hasUnsafeCharacter(raw)) return null;

  let url: URL;

  try {
    url = new URL(raw, PROBE_ORIGIN);
  } catch {
    return null;
  }

  if (url.origin !== PROBE_ORIGIN) return null;

  let pathname: string;

  try {
    pathname = decodeURIComponent(url.pathname).toLowerCase();
  } catch {
    return null;
  }

  if (pathname === '/login' || pathname.startsWith('/login/')) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}
